import React, { useState, useEffect, useCallback, useRef } from "react";
import { Bar, Line } from "react-chartjs-2";
import { Chart, registerables } from "chart.js";
import { BeatLoader } from "react-spinners";
import "../../../../../styles/count_graphs.css";
import { getPolygonBorderColor } from "./DwellTimeUtils";
import {
  BiFullscreen,
  BiExitFullscreen,
  BiLineChart,
  BiBarChartAlt2,
} from "react-icons/bi";
import { peopleDwellColor } from "../../../../../config";

const DwellChart = ({
  selectedYear,
  monthlyDwellData,
  monthlyDataLoading,
  selectedMonth,
  setSelectedMonth,
  lineChart,
  monthlyAverage,
  monthlyIncrease,
  isLineChart,
  toggleLineChart,
}) => {
  const [chartData, setChartData] = useState();
  const [isFullscreen, setIsFullscreen] = useState(false);

  const getMonthName = (monthNumber) => {
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return months[monthNumber - 1];
  };

  const smallScreen = window.innerWidth < 786;

  const chartRef = useRef(null);

  // Assuming that the monthly dwell data is just a list rather than a list of maps.
  const prepareMonthlyDwellChart = () => {
    if (!monthlyDwellData || monthlyDwellData.length === 0) {
      return { labels: [], datasets: [] };
    }

    const monthLabels = monthlyDwellData.map((_, i) => getMonthName(i + 1));
    //seems like this needs to be a set afterall.
    const polygonLabels = new Map();

    monthlyDwellData.forEach((month) => {
      month.forEach((polygon) => {
        polygonLabels.set(polygon.polygon_id, polygon.polygon_name);
      });
    });

    // Make a bar per month consisting of poygon data stacked on top of each other.

    const datasets = () => {
      let index = 0;
      const totalPolygons = polygonLabels.size;
      const result = [];

      for (const [polygon_id, polygon_name] of polygonLabels) {
        result.push({
          label: `${polygon_name}`,
          data: monthlyDwellData.map((month) => {
            const polygon = month.find((p) => p.polygon_id === polygon_id);
            return polygon ? (polygon.average_dwell_time / 60).toFixed(1) : 0;
          }),
          backgroundColor: getPolygonBorderColor(polygon_id),
          borderColor: getPolygonBorderColor(polygon_id),
          borderWidth: 3,
          tension: 0.3,
        });
        index++;
      }
      return result;
    };

    return {
      labels: monthLabels,
      datasets: datasets(),
    };
  };

  const prepareMonthlyDwellBarChart = () => {
    if (!monthlyDwellData || monthlyDwellData.length === 0) {
      return { labels: [], datasets: [] };
    }

    const monthLabels = monthlyDwellData.map((_, i) => getMonthName(i + 1));
    //seems like this needs to be a set afterall.
    const polygonLabels = new Map();

    monthlyDwellData.forEach((month) => {
      month.forEach((polygon) => {
        polygonLabels.set(polygon.polygon_id, polygon.polygon_name);
      });
    });

    // Make a bar per month consisting of poygon data stacked on top of each other.

    const datasets = () => {
      let index = 0;
      const totalPolygons = polygonLabels.size;
      const result = [];

      for (const [polygon_id, polygon_name] of polygonLabels) {
        result.push({
          label: `Polygon ${polygon_name}`,
          data: monthlyDwellData.map((month) => {
            const polygon = month.find((p) => p.polygon_id === polygon_id);
            return polygon ? (polygon.average_dwell_time / 60).toFixed(2) : 0;
          }),
          backgroundColor: getPolygonBorderColor(polygon_id),
          borderColor: "#ccc",
          borderWidth: 1,
        });
        index++;
      }
      return result;
    };

    return {
      labels: monthLabels,
      datasets: datasets(),
    };
  };

  useEffect(() => {
    if (monthlyDwellData.length) {
      const preparedChart = prepareMonthlyDwellChart();
      if (preparedChart) {
        setChartData(preparedChart);
      }
    }
  }, [monthlyDwellData]);

  const handleMonthSectionClick = (chart, event) => {
    const nativeEvent = event.native || event;

    // Get the x-coordinate of the click within the chart area

    const rect = chart.canvas.getBoundingClientRect();

    let x;
    if (nativeEvent.clientX !== undefined) {
      x = nativeEvent.clientX - rect.left;
    } else if (event.x !== undefined) {
      x = event.x - rect.left;
    } else {
      // For Chart.js v3, try extracting from the event object directly
      const position = chart.getRelativePosition(event, chart);
      x = position.x;
    }

    // Get chart area dimensions
    const chartArea = chart.chartArea;

    // Ensure the click is within the chart area
    if (x >= chartArea.left && x <= chartArea.right) {
      // Calculate which month was clicked
      const monthWidth =
        (chartArea.right - chartArea.left) / chart.data.labels.length;
      const monthIndex = Math.floor((x - chartArea.left) / monthWidth);

      // Get the month label
      const monthLabel = chart.data.labels[monthIndex];

      switch (monthLabel) {
        case "Jan":
          setSelectedMonth(1);
          break;
        case "Feb":
          setSelectedMonth(2);
          break;
        case "Mar":
          setSelectedMonth(3);
          break;
        case "Apr":
          setSelectedMonth(4);
          break;
        case "May":
          setSelectedMonth(5);
          break;
        case "Jun":
          setSelectedMonth(6);
          break;
        case "Jul":
          setSelectedMonth(7);
          break;
        case "Aug":
          setSelectedMonth(8);
          break;
        case "Sep":
          setSelectedMonth(9);
          break;
        case "Oct":
          setSelectedMonth(10);
          break;
        case "Nov":
          setSelectedMonth(11);
          break;
        case "Dec":
          setSelectedMonth(12);
          break;
      }
    }
  };

  const handleChartHover = (event) => {
    if (chartRef.current) {
      const chart = chartRef.current;
      const rect = chart.canvas.getBoundingClientRect();
      const x = event.clientX - rect.left;

      // Store the x position for use in the plugin
      chart.hoverX = event.clientX - rect.left;

      // Store the x position for use in the plugin
      chart.hoverX = x;

      // Check if we're within the chart area
      const { chartArea } = chart;
      if (x >= chartArea.left && x <= chartArea.right) {
        // We're hovering over a month section, change cursor to pointer
        event.target.style.cursor = "pointer";
      } else {
        // Not over a month section, use default cursor
        event.target.style.cursor = "default";
      }

      // Trigger a redraw to show the highlight
      chart.update("none"); // 'none' mode for fast updates
    }
  };

  // Add this function to clear highlight when mouse leaves
  const handleChartMouseLeave = () => {
    if (chartRef.current) {
      const chart = chartRef.current;
      chart.hoverX = null;
      chart.canvas.style.cursor = "default";
      chart.update("none");
    }
  };

  Chart.register(...registerables);

  return (
    <div className="monthly-bar-chart-container">
      <div
        className={`count-bar-chart-wrapper ${
          isFullscreen ? "fullscreen-container" : ""
        }`}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "1rem",
          }}
        >
          {/* Left: Chart title */}
          <div
            className="graph-title"
            style={{ flex: "1 1 auto", minWidth: "200px" }}
          >
            Monthly Average Dwell Time
          </div>

          {/* Right: Buttons + Percentage */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "1rem",
              justifyContent: "flex-end",
              gap: "1rem",
              flex: "1 1 auto",
              minWidth: "100px",
            }}
          >
            <button
              className="toggle-chart-button"
              onClick={() => toggleLineChart("month")}
            >
              {isLineChart ? (
                <BiBarChartAlt2
                  size={24}
                  title="Switch to Bar Chart"
                  color="white"
                />
              ) : (
                <BiLineChart
                  size={24}
                  title="Switch to Line Chart"
                  color="white"
                />
              )}
            </button>
            <button
              className="toggle-chart-button"
              onClick={() => setIsFullscreen((prev) => !prev)}
              title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
            >
              {isFullscreen ? (
                <BiExitFullscreen size={24} color="white" />
              ) : (
                <BiFullscreen size={24} color="white" />
              )}
            </button>
          </div>
        </div>

        <div
          className={
            monthlyDataLoading
              ? "dwell-monthly-bar-chart-container-loading"
              : ""
          }
          onMouseMove={handleChartHover}
          onMouseLeave={handleChartMouseLeave}
        >
          <div className="chart-total-container">
            <div className="chart-total-pill">
              <div
                className="chart-total-label"
                style={{ color: peopleDwellColor }}
              >
                {" "}
                Avg Dwell Time (min):{" "}
                {monthlyDwellData.length > 0
                  ? (
                      monthlyDwellData
                        .flat()
                        .reduce(
                          (sum, item) => sum + item.average_dwell_time,
                          0
                        ) /
                      monthlyDwellData.flat().length /
                      60
                    ).toFixed(1)
                  : "0.0"}
              </div>
            </div>
          </div>
          {lineChart ? (
            <Line
              ref={chartRef}
              data={chartData || { labels: [], datasets: [] }} // Fetch prepared data
              options={{
                responsive: true, // Ensure the chart is responsive
                maintainAspectRatio: true, // Ensure the chart maintains its aspect ratio
                scales: {
                  x: {
                    ticks: {
                      color: (context) => {
                        // Change this to accomodate multiple polygons per month.
                        return context.tick.label ===
                          getMonthName(selectedMonth)
                          ? "#32de84"
                          : "#F8F8F8";
                      },
                      font: {
                        weight: (context) =>
                          context.tick.label === getMonthName(selectedMonth)
                            ? "bold"
                            : "normal",
                        size: (context) =>
                          context.tick.label === getMonthName(selectedMonth)
                            ? 16
                            : 14,
                      },
                    },
                    title: {
                      display: true,
                      text: "Months",
                      font: {
                        size: 14,
                        weight: "normal",
                      },
                      color: "#ffffffff",
                    },
                    grid: {
                      display: true,
                      drawBorder: true,
                      drawOnChartArea: false,
                      color: "#aaa",
                    },
                  },
                  y: {
                    ticks: {
                      color: "#F8F8F8",
                    },
                    title: {
                      display: true,
                      text: "Minutes",
                      font: {
                        size: 14,
                        weight: "normal",
                      },
                      color: "#ffffffff",
                    },
                  },
                },
                plugins: {
                  tooltip: {
                    mode: "nearest", // Show tooltip for all datasets at the same index
                  },
                  legend: {
                    position: "top", // Position the legend at the top
                    labels: {
                      color: "white",
                      font: {
                        size: smallScreen ? 8 : 14, // Adjust font size of legend labels
                      },
                      boxWidth: smallScreen ? 8 : 40, // Adjust width of legend marker box
                      boxHeight: smallScreen ? 8 : 10, // Adjust height of legend marker box (optional, defaults to boxWidth)
                    },
                  },
                  monthHighlight: {
                    highlightColor: "rgba(50, 222, 132, 0.2",
                  },
                },

                onClick: (event, elements) => {
                  const chart = chartRef.current;
                  if (chart) {
                    handleMonthSectionClick(chart, event);
                  }
                },
              }}
            />
          ) : (
            <Bar
              ref={chartRef}
              data={chartData || { labels: [], datasets: [] }} // Fetch prepared data
              options={{
                responsive: true, // Ensure the chart is responsive
                maintainAspectRatio: true, // Ensure the chart maintains its aspect ratio
                scales: {
                  x: {
                    stacked: false,
                    ticks: {
                      color: (context) => {
                        // Change this to accomodate multiple polygons per month.
                        return context.tick.label ===
                          getMonthName(selectedMonth)
                          ? "#32de84"
                          : "#F8F8F8";
                      },
                      font: {
                        weight: (context) =>
                          context.tick.label === getMonthName(selectedMonth)
                            ? "bold"
                            : "normal",
                        size: (context) =>
                          context.tick.label === getMonthName(selectedMonth)
                            ? 16
                            : 14,
                      },
                    },
                    title: {
                      display: true,
                      text: "Months",
                      font: {
                        size: 14,
                        weight: "normal",
                      },
                      color: "#ffffffff",
                    },
                    grid: {
                      display: true,
                      drawBorder: true,
                      drawOnChartArea: false,
                      color: "#aaa",
                    },
                  },
                  y: {
                    ticks: {
                      color: "#F8F8F8",
                    },
                    stacked: false,
                    title: {
                      display: true,
                      text: "Minutes",
                      font: {
                        size: 14,
                        weight: "normal",
                      },
                      color: "#ffffffff",
                    },
                  },
                },
                plugins: {
                  tooltip: {
                    mode: "nearest", // Show tooltip for all datasets at the same index
                  },
                  legend: {
                    position: "top", // Position the legend at the top
                    labels: {
                      color: "white",
                      font: {
                        size: smallScreen ? 8 : 14, // Adjust font size of legend labels
                      },
                      boxWidth: smallScreen ? 8 : 40, // Adjust width of legend marker box
                      boxHeight: smallScreen ? 8 : 10, // Adjust height of legend marker box (optional, defaults to boxWidth)
                    },
                  },
                  monthHighlight: {
                    highlightColor: "rgba(50, 222, 132, 0.2",
                  },
                },

                onClick: (event, elements) => {
                  const chart = chartRef.current;
                  if (chart) {
                    handleMonthSectionClick(chart, event);
                  }
                },
              }}
            />
          )}
          {/* <div className='dwell-chart-footer-container'>
                        <div className='dwell-chart-footer-label'>Average dwell time for {getMonthName(selectedMonth)} {selectedYear}:</div>
                        <div className='dwell-chart-footer-value'>{monthlyAverage}</div>
                    </div> */}
          {monthlyDataLoading && (
            <div id="dwell-monthly-loading-overlay">
              <BeatLoader
                color={"white"}
                loading={monthlyDataLoading}
                size={15}
              />
            </div>
          )}
        </div>
      </div>
      {/* </div> */}
    </div>
  );
};

// Memoize component to prevent unnecessary re-renders
export default React.memo(DwellChart, (prevProps, nextProps) => {
  return (
    prevProps.selectedYear === nextProps.selectedYear &&
    prevProps.selectedMonth === nextProps.selectedMonth &&
    prevProps.monthlyDataLoading === nextProps.monthlyDataLoading &&
    prevProps.lineChart === nextProps.lineChart &&
    prevProps.isLineChart === nextProps.isLineChart &&
    prevProps.monthlyAverage === nextProps.monthlyAverage &&
    prevProps.monthlyIncrease === nextProps.monthlyIncrease &&
    prevProps.monthlyDwellData === nextProps.monthlyDwellData
  );
});
